/**
 * Ambient palettes: one light + one dark variant per time-of-day window.
 * The DesignContext interpolates between window midpoints each minute so
 * palette drifts continuously; these tables are the keyframes.
 */

export const TOD_WINDOWS = [
  { id: 'night', label: 'Night', start: 21.0, end: 4.5 },
  { id: 'dawn', label: 'Dawn', start: 4.5, end: 7.0 },
  { id: 'morning', label: 'Morning', start: 7.0, end: 11.0 },
  { id: 'midday', label: 'Midday', start: 11.0, end: 16.0 },
  { id: 'golden', label: 'Golden hour', start: 16.0, end: 18.5 },
  { id: 'dusk', label: 'Dusk', start: 18.5, end: 21.0 },
];

export const TOD_IDS = TOD_WINDOWS.map((w) => w.id);

/** Resolve the time-of-day window + progress (0..1 through it) for an hour. */
export function resolveTOD(hourFloat) {
  for (const w of TOD_WINDOWS) {
    const inWindow =
      w.start <= w.end
        ? hourFloat >= w.start && hourFloat < w.end
        : hourFloat >= w.start || hourFloat < w.end;
    if (inWindow) {
      const span = (w.end - w.start + 24) % 24 || 24;
      const through = ((hourFloat - w.start + 24) % 24) / span;
      return { id: w.id, label: w.label, progress: through };
    }
  }
  // The table covers the clock; fall back defensively to midday.
  return { id: 'midday', label: 'Midday', progress: 0.5 };
}

// --- color helpers (hex only; keep dependency-free) ---

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }) {
  const c = (n) =>
    Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function hexAlpha(hex, a) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function lerpColor(a, b, t) {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex({
    r: ca.r + (cb.r - ca.r) * t,
    g: ca.g + (cb.g - ca.g) * t,
    b: ca.b + (cb.b - ca.b) * t,
  });
}

/** Mix toward perceptual gray; factor 1 = unchanged, 0 = fully gray. */
export function desaturate(hex, factor = 1) {
  const { r, g, b } = hexToRgb(hex);
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return rgbToHex({
    r: r + (luma - r) * (1 - factor),
    g: g + (luma - g) * (1 - factor),
    b: b + (luma - b) * (1 - factor),
  });
}

/** WCAG contrast ratio between two hex colors. */
export function contrastRatio(hexA, hexB) {
  const lum = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const ch = [r, g, b].map((v) => {
      const s = v / 255;
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  };
  const la = lum(hexA);
  const lb = lum(hexB);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Darken until the color meets `ratio` against `bg`.
 * Returns the original hex when it already passes.
 */
export function ensureContrast(hex, bg, ratio = 4.5) {
  if (contrastRatio(hex, bg) >= ratio) return hex;
  const base = hexToRgb(hex);
  for (let k = 1; k <= 100; k += 1) {
    const candidate = rgbToHex({
      r: base.r * (1 - k / 100),
      g: base.g * (1 - k / 100),
      b: base.b * (1 - k / 100),
    });
    if (contrastRatio(candidate, bg) >= ratio) return candidate;
  }
  return '#000000';
}

// --- palettes: dark + light per window ---

const fontDisplay = '"Plus Jakarta Sans", system-ui, sans-serif';
const fontBody = '"Nunito", system-ui, sans-serif';

/**
 * Raw keyframe stops per window×mode. skyTop/Mid/Bottom double as the
 * light-mode page gradient and the login sky.
 */
const KEYFRAMES = {
  night: {
    dark: {
      bg: '#050810', text: '#e6eef8', muted: '#8a9bb0',
      accent: '#4ec8d9', accent2: '#8f7ff0', glow: '#4ec8d9',
      sky: ['#02040a', '#060b18', '#0b1226'],
    },
    light: {
      bg: '#eef1f6', text: '#1a1f2b', muted: '#5c667a',
      accent: '#0f8f97', accent2: '#5f3dc4', glow: '#0f8f97',
      sky: ['#dfe5ee', '#e8ecf3', '#f2f4f8'],
    },
  },
  dawn: {
    dark: {
      bg: '#0d0a14', text: '#f2eaf3', muted: '#a394ad',
      accent: '#f08eb1', accent2: '#4ec8d9', glow: '#f08eb1',
      sky: ['#0a0712', '#171026', '#2b1a33'],
    },
    light: {
      bg: '#faf2f0', text: '#2b1a22', muted: '#6e5b64',
      accent: '#d6336c', accent2: '#0f8f97', glow: '#d6336c',
      sky: ['#fde8e6', '#f9eef0', '#f5f3f6'],
    },
  },
  morning: {
    dark: {
      bg: '#080d1a', text: '#e8eef8', muted: '#8a9bb0',
      accent: '#c44ea8', accent2: '#4ec8d9', glow: '#4ec8d9',
      sky: ['#050a16', '#0a1020', '#101830'],
    },
    light: {
      bg: '#f6f8fa', text: '#1a1f2b', muted: '#5c667a',
      accent: '#c0267e', accent2: '#0f8f97', glow: '#c0267e',
      sky: ['#eef4fb', '#f4f7fa', '#f8fafc'],
    },
  },
  midday: {
    dark: {
      bg: '#0a1020', text: '#e6eef8', muted: '#8a9bb0',
      accent: '#d94fb0', accent2: '#4ec8d9', glow: '#4ec8d9',
      sky: ['#060b18', '#0c1226', '#101a34'],
    },
    light: {
      bg: '#f4f7fa', text: '#1a1f2b', muted: '#5c667a',
      accent: '#c0267e', accent2: '#0f8f97', glow: '#c0267e',
      sky: ['#f0f4f9', '#f5f7fa', '#fafbfc'],
    },
  },
  golden: {
    dark: {
      bg: '#120e0b', text: '#f4ede6', muted: '#a89c90',
      accent: '#f2a65a', accent2: '#e86a92', glow: '#f2a65a',
      sky: ['#0d0908', '#1a120e', '#2a1c14'],
    },
    light: {
      bg: '#faf4e9', text: '#2e241a', muted: '#6f6154',
      accent: '#d9480f', accent2: '#c0267e', glow: '#d9480f',
      sky: ['#f7e9d2', '#faf3e7', '#fdf8ef'],
    },
  },
  dusk: {
    dark: {
      bg: '#0e0a16', text: '#ece6f4', muted: '#9c92ae',
      accent: '#b48cf2', accent2: '#f08eb1', glow: '#b48cf2',
      sky: ['#080512', '#150f24', '#241a33'],
    },
    light: {
      bg: '#f5f0f7', text: '#241d2e', muted: '#655c72',
      accent: '#9c36b5', accent2: '#d6336c', glow: '#9c36b5',
      sky: ['#ece0f3', '#f2eaf6', '#f7f2f9'],
    },
  },
};

function buildPalette(mode, c) {
  const dark = mode === 'dark';
  const [skyTop, skyMid, skyBottom] = c.sky;
  const focus = dark ? c.accent : ensureContrast(c.accent, c.bg, 3.0);

  const shellBg = dark
    ? [
        `radial-gradient(1100px 620px at 8% -8%, ${hexAlpha(c.accent, 0.11)}, transparent 58%)`,
        `radial-gradient(900px 560px at 96% 12%, ${hexAlpha(c.accent2, 0.09)}, transparent 60%)`,
        `radial-gradient(700px 480px at 50% 100%, ${hexAlpha(c.accent2, 0.06)}, transparent 55%)`,
        `linear-gradient(165deg, ${skyTop} 0%, ${skyMid} 42%, ${skyBottom} 100%)`,
      ].join(', ')
    : `linear-gradient(180deg, ${skyTop}, ${skyMid}, ${skyBottom})`;

  return {
    '--ada-bg': c.bg,
    '--ada-bg-soft': dark ? hexAlpha(c.accent2, 0.1) : '#ffffff',
    '--ada-sidebar': dark ? hexAlpha(c.accent2, 0.08) : '#ffffff',
    '--ada-border': dark
      ? 'rgba(180, 210, 240, 0.12)'
      : 'rgba(26, 31, 43, 0.1)',
    '--ada-text': c.text,
    '--ada-muted': c.muted,
    '--ada-accent': c.accent,
    '--ada-accent-2': c.accent2,
    '--ada-focus': focus,
    '--ada-user-bubble': dark ? hexAlpha(c.accent, 0.1) : '#e8eef8',
    '--ada-ai-bubble': dark ? 'rgba(255,255,255,0.035)' : 'transparent',
    '--ada-composer-bg': dark ? hexAlpha(c.accent2, 0.16) : '#ffffff',
    '--ada-glow': hexAlpha(c.glow, dark ? 0.1 : 0.12),
    '--ada-font-display': fontDisplay,
    '--ada-font-body': fontBody,
    '--ada-radius': dark ? '16px' : '12px',
    '--ada-sidebar-width': dark ? '224px' : '214px',
    '--ada-shell-bg': shellBg,
    '--ada-sky-top': skyTop,
    '--ada-sky-mid': skyMid,
    '--ada-sky-bottom': skyBottom,
    '--ada-sky-glow': hexAlpha(c.accent, dark ? 0.35 : 0.25),
  };
}

export const TOD_PALETTES = Object.fromEntries(
  Object.entries(KEYFRAMES).map(([id, modes]) => [
    id,
    { dark: buildPalette('dark', modes.dark), light: buildPalette('light', modes.light) },
  ])
);

/** Keys with plain hex values — interpolatable between windows. */
const COLOR_KEYS = new Set([
  '--ada-bg',
  '--ada-text',
  '--ada-muted',
  '--ada-accent',
  '--ada-accent-2',
  '--ada-focus',
  '--ada-sky-top',
  '--ada-sky-mid',
  '--ada-sky-bottom',
]);

function windowIndex(id) {
  const i = TOD_WINDOWS.findIndex((w) => w.id === id);
  return i < 0 ? TOD_WINDOWS.findIndex((w) => w.id === 'midday') : i;
}

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

export function getTODPalette(winId, mode) {
  const win = TOD_PALETTES[winId] || TOD_PALETTES.midday;
  return win[mode] || win.dark;
}

/**
 * Continuous palette for `progress` (0..1) through `winId`:
 * hold at this window's palette until its midpoint, then ease toward the
 * next window's palette reaching it exactly at the boundary.
 */
export function interpolatePalette(winId, mode, progress) {
  const cur = getTODPalette(winId, mode);
  const t = Math.min(1, Math.max(0, progress));
  const blend = t <= 0.5 ? 0 : easeInOut((t - 0.5) / 0.5);
  if (blend <= 0) return { ...cur };

  const next = getTODPalette(
    TOD_WINDOWS[(windowIndex(winId) + 1) % TOD_WINDOWS.length].id,
    mode
  );
  const out = {};
  for (const key of Object.keys(cur)) {
    out[key] = COLOR_KEYS.has(key)
      ? lerpColor(cur[key], next[key], blend)
      : cur[key];
  }
  out['--ada-shell-bg'] = shellBgFrom(out, mode);
  out['--ada-sky-glow'] = hexAlpha(out['--ada-accent'], mode === 'dark' ? 0.35 : 0.25);
  return out;
}

/**
 * Apply weather tint: dim glow and desaturate accents for overcast/rain.
 * `tint` is CONDITION_TINT[condition] (1 = clear → unchanged).
 */
export function applyWeatherTint(vars, tint = 1, mode = 'dark') {
  if (!tint || tint >= 1) return vars;
  const out = { ...vars };
  out['--ada-accent'] = desaturate(vars['--ada-accent'], tint);
  out['--ada-accent-2'] = desaturate(vars['--ada-accent-2'], tint);
  out['--ada-glow'] = hexAlpha(desaturate(vars['--ada-glow'] ? stripAlpha(vars['--ada-glow']) : vars['--ada-accent'], tint), 0.05 + 0.07 * tint);
  out['--ada-shell-bg'] = shellBgFrom(out, mode);
  out['--ada-sky-glow'] = hexAlpha(out['--ada-accent'], mode === 'dark' ? 0.35 : 0.25);
  return out;
}

/** rgba(...) → #rrggbb (drops alpha). */
function stripAlpha(value) {
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(value);
  if (!m) return value.startsWith('#') ? value : '#000000';
  return rgbToHex({ r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) });
}

/** Rebuild composite gradients from already-interpolated stops. */
function shellBgFrom(vars, mode) {
  const sky = [vars['--ada-sky-top'], vars['--ada-sky-mid'], vars['--ada-sky-bottom']];
  if (mode !== 'dark') {
    return `linear-gradient(180deg, ${sky.join(', ')})`;
  }
  return [
    `radial-gradient(1100px 620px at 8% -8%, ${hexAlpha(vars['--ada-accent'], 0.11)}, transparent 58%)`,
    `radial-gradient(900px 560px at 96% 12%, ${hexAlpha(vars['--ada-accent-2'], 0.09)}, transparent 60%)`,
    `radial-gradient(700px 480px at 50% 100%, ${hexAlpha(vars['--ada-accent-2'], 0.06)}, transparent 55%)`,
    `linear-gradient(165deg, ${sky[0]} 0%, ${sky[1]} 42%, ${sky[2]} 100%)`,
  ].join(', ');
}
